type AudioBusName = "master" | "music" | "sfx" | "ambient";

export type SfxCue = "button" | "carousel" | "nav" | "transition";

type AudioManagerOptions = {
  ambientVolume: number;
  enabled: boolean;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
};

const defaultOptions: AudioManagerOptions = {
  ambientVolume: 0.28,
  enabled: false,
  masterVolume: 0.65,
  musicVolume: 0.48,
  sfxVolume: 0.72
};

const sfxUrls: Partial<Record<SfxCue, string>> = {
  button: "/audio/sfx/ui/buttonClick.wav",
  //transition: "/audio/sfx/ui/transition.mp3"
};

function getAudioContextConstructor() {
  if (typeof window === "undefined") {
    return null;
  }

  const audioWindow = window as Window & {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return audioWindow.AudioContext ?? audioWindow.webkitAudioContext ?? null;
}

function setGain(gainNode: GainNode | null, value: number, rampTime = 0.035) {
  if (!gainNode) {
    return;
  }

  const now = gainNode.context.currentTime;
  gainNode.gain.cancelScheduledValues(now);
  gainNode.gain.setTargetAtTime(Math.max(value, 0), now, rampTime);
}

class AudioManager {
  private audioContext: AudioContext | null = null;
  private bufferCache = new Map<string, AudioBuffer>();
  private enabled = defaultOptions.enabled;
  private unlocked = false;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private scrollOscillator: OscillatorNode | null = null;
  private scrollFilter: BiquadFilterNode | null = null;
  private musicElement: HTMLAudioElement | null = null;
  private musicElementSource: MediaElementAudioSourceNode | null = null;
  private musicPlaybackToken = 0;
  private musicUrl: string | null = null;
  private volumes = {
    ambient: defaultOptions.ambientVolume,
    master: defaultOptions.masterVolume,
    music: defaultOptions.musicVolume,
    sfx: defaultOptions.sfxVolume
  };

  configure(options: Partial<AudioManagerOptions>) {
    this.enabled = options.enabled ?? this.enabled;
    this.volumes.master = options.masterVolume ?? this.volumes.master;
    this.volumes.music = options.musicVolume ?? this.volumes.music;
    this.volumes.sfx = options.sfxVolume ?? this.volumes.sfx;
    this.volumes.ambient = options.ambientVolume ?? this.volumes.ambient;
    this.syncGains();
  }

  get isUnlocked() {
    return this.unlocked;
  }

  async unlock() {
    if (this.unlocked) {
      return true;
    }

    const AudioContextConstructor = getAudioContextConstructor();
    if (!AudioContextConstructor) {
      return false;
    }

    if (!this.audioContext) {
      this.audioContext = new AudioContextConstructor();
      this.createBuses();
    }

    const audioContext = this.audioContext;
    if (!audioContext) {
      return false;
    }

    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    this.unlocked = true;
    this.syncGains();
    this.startScrollBed();
    if (this.enabled && this.musicUrl) {
      void this.playMusic(this.musicUrl);
    }
    return true;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    this.syncGains();

    if (!enabled) {
      this.musicPlaybackToken += 1;
      this.stopMusic();
      this.setScrollIntensity(0);
      return;
    }

    if (this.unlocked && this.musicUrl) {
      void this.playMusic(this.musicUrl);
    }
  }

  setVolume(bus: Exclude<AudioBusName, "master"> | "master", value: number) {
    this.volumes[bus] = Math.min(Math.max(value, 0), 1);
    this.syncGains();
  }

  setMusicUrl(url: string | null) {
    if (this.musicUrl === url) {
      return;
    }

    this.musicUrl = url;
    this.musicPlaybackToken += 1;
    this.stopMusic();
    if (url) {
      this.prepareMusicElement(url);
    }

    if (this.enabled && this.unlocked && url) {
      void this.playMusic(url);
    }
  }

  async playMusic(url: string) {
    if (!this.enabled || !this.unlocked || !this.audioContext || !this.musicGain) {
      return;
    }

    const playbackToken = this.musicPlaybackToken;
    const musicElement = this.prepareMusicElement(url);
    if (!musicElement) {
      return;
    }

    try {
      await musicElement.play();
      if (!this.enabled || playbackToken !== this.musicPlaybackToken || this.musicUrl !== url) {
        this.stopMusic();
      }
    } catch {
      // Browser autoplay policy or unsupported source; sound can be retried on the next user gesture.
    }
  }

  stopMusic() {
    if (!this.musicElement) {
      return;
    }

    this.musicElement.pause();
    this.musicElement.currentTime = 0;
  }

  playSfx(cue: SfxCue) {
    if (!this.enabled || !this.unlocked || !this.audioContext || !this.sfxGain) {
      return;
    }

    const sfxUrl = sfxUrls[cue];
    if (sfxUrl) {
      void this.playBufferSfx(sfxUrl, () => this.playSynthSfx(cue));
      return;
    }

    this.playSynthSfx(cue);
  }

  private playSynthSfx(cue: SfxCue) {
    if (cue === "transition") {
      // this.playSweep(160, 62, 0.42, 0.08);
      // return;
    }

    if (cue === "nav") {
      // this.playTone(392, 0.06, 0.035, "triangle");
      // window.setTimeout(() => this.playTone(587, 0.07, 0.028, "sine"), 42);
      //return;
    }

    if (cue === "carousel") {
      this.playTone(880, 0.055, 0.025, "sine");
      return;
    }

    //this.playTone(620, 0.07, 0.024, "triangle");
  }

  setScrollIntensity(scrollVelocity: number) {
    if (!this.ambientGain || !this.scrollFilter || !this.enabled || !this.unlocked) {
      setGain(this.ambientGain, 0, 0.08);
      return;
    }

    const speed = Math.min(Math.abs(scrollVelocity) / 1.4, 1);
    const level = speed * this.volumes.master * this.volumes.ambient * 0.08;
    setGain(this.ambientGain, level, 0.12);
    this.scrollFilter.frequency.setTargetAtTime(180 + speed * 420, this.scrollFilter.context.currentTime, 0.12);
  }

  private createBuses() {
    if (!this.audioContext) {
      return;
    }

    this.masterGain = this.audioContext.createGain();
    this.musicGain = this.audioContext.createGain();
    this.sfxGain = this.audioContext.createGain();
    this.ambientGain = this.audioContext.createGain();

    this.musicGain.connect(this.masterGain);
    this.sfxGain.connect(this.masterGain);
    this.ambientGain.connect(this.masterGain);
    this.masterGain.connect(this.audioContext.destination);
    this.syncGains();
  }

  private syncGains() {
    const enabledMultiplier = this.enabled ? 1 : 0;
    setGain(this.masterGain, this.volumes.master * enabledMultiplier);
    setGain(this.musicGain, this.volumes.music);
    setGain(this.sfxGain, this.volumes.sfx);
  }

  private startScrollBed() {
    if (!this.audioContext || !this.ambientGain || this.scrollOscillator) {
      return;
    }

    this.scrollOscillator = this.audioContext.createOscillator();
    this.scrollFilter = this.audioContext.createBiquadFilter();
    this.scrollOscillator.type = "sine";
    this.scrollOscillator.frequency.value = 72;
    this.scrollFilter.type = "lowpass";
    this.scrollFilter.frequency.value = 180;
    this.scrollOscillator.connect(this.scrollFilter);
    this.scrollFilter.connect(this.ambientGain);
    setGain(this.ambientGain, 0);
    this.scrollOscillator.start();
  }

  private prepareMusicElement(url: string) {
    if (!this.audioContext || !this.musicGain) {
      return null;
    }

    if (!this.musicElement) {
      this.musicElement = new Audio();
      this.musicElement.loop = true;
      this.musicElement.preload = "none";
      this.musicElement.crossOrigin = "anonymous";
      this.musicElementSource = this.audioContext.createMediaElementSource(this.musicElement);
      this.musicElementSource.connect(this.musicGain);
    }

    if (this.musicElement.src !== new URL(url, window.location.href).href) {
      this.musicElement.pause();
      this.musicElement.src = url;
      this.musicElement.load();
    }

    return this.musicElement;
  }

  private async playBufferSfx(url: string, fallback: () => void) {
    if (!this.audioContext || !this.sfxGain) {
      return;
    }

    const buffer = await this.loadBuffer(url);
    if (!buffer || !this.audioContext || !this.sfxGain) {
      fallback();
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.sfxGain);
    source.start();
  }

  private playTone(frequency: number, duration: number, level: number, type: OscillatorType) {
    if (!this.audioContext || !this.sfxGain) {
      return;
    }

    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(level, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.sfxGain);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private playSweep(startFrequency: number, endFrequency: number, duration: number, level: number) {
    if (!this.audioContext || !this.sfxGain) {
      return;
    }

    const now = this.audioContext.currentTime;
    const oscillator = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    oscillator.type = "sawtooth";
    oscillator.frequency.setValueAtTime(startFrequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(endFrequency, now + duration);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(level, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.sfxGain);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.03);
  }

  private async loadBuffer(url: string) {
    if (!this.audioContext) {
      return null;
    }

    const cachedBuffer = this.bufferCache.get(url);
    if (cachedBuffer) {
      return cachedBuffer;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.bufferCache.set(url, audioBuffer);
      return audioBuffer;
    } catch {
      return null;
    }
  }
}

export const audioManager = new AudioManager();
